import type { MouseEvent, ReactElement } from 'react';
import { connect } from 'react-redux';
import { FirstPage, NextPage, Page, Pages, PaginationContainer, PrevPage } from '@finn-no/troika-react-pagination';

import type { AccommodationState, Filters } from '@/interfaces';
import { fetchHotels } from '@/ducks/searchResults/actions';
import { actions as filterActions } from '@/state/filterActions';
import { filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';

export interface PaginationProps {
    numberOfHotels: number;
    filters: Filters;
    mapOpen: boolean;
    setPage: (page?: number) => void;
    triggerFetch: (resetMap: boolean) => void;
}

export const Pagination = ({ numberOfHotels, filters, mapOpen, setPage, triggerFetch }: PaginationProps) => {
    const currentPage = filters.page || 1;

    const numberOfPages = Math.floor(numberOfHotels / 20 + 1);
    const setFocusOnFirstSearchResultsItem = (): void => {
        const searchResults = document.getElementById('searchResults');
        const firstElement = searchResults?.getElementsByTagName('button')[0];
        if (firstElement) {
            (firstElement as HTMLElement).focus();
        }
    };

    const renderNewPage = async (page: number): Promise<void> => {
        setPage(page === 1 ? undefined : page);
        await triggerFetch(false);
        await setFocusOnFirstSearchResultsItem();
        window.scroll(0, 0);
    };

    const createClick =
        (page: number) =>
        (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
            event.preventDefault();
            return renderNewPage(page);
        };

    if (Number.isNaN(numberOfPages) || numberOfPages <= 1) {
        return null;
    }

    return (
        <PaginationContainer currentPage={currentPage} lastPage={numberOfPages} data-testid="pagination">
            <FirstPage href="?page=1" onClick={createClick(1)} data-testid="firstPage" aria-label="Første side" />
            <PrevPage
                href={`?${filtersToResultPageBrowserQueryParams({ ...filters, page: currentPage - 1 }, mapOpen)}`}
                onClick={createClick(currentPage - 1)}
                data-testid="prevPage"
                aria-label="Forrige side"
            />
            <Pages data-testid="pageNumbers">
                {(page: number): ReactElement => (
                    <Page
                        href={`?${filtersToResultPageBrowserQueryParams({ ...filters, page }, mapOpen)}`}
                        onClick={createClick(page)}
                        data-testid={currentPage === page ? 'selected' : `page${page}`}
                        aria-label={`Side ${page}`}
                    />
                )}
            </Pages>
            <NextPage
                href={`?${filtersToResultPageBrowserQueryParams({ ...filters, page: currentPage + 1 }, mapOpen)}`}
                onClick={createClick(currentPage + 1)}
                data-testid="nextPage"
                aria-label="Neste side"
            />
        </PaginationContainer>
    );
};

const mapStateToProps = (state: AccommodationState): Pick<PaginationProps, 'numberOfHotels' | 'filters' | 'mapOpen'> => ({
    numberOfHotels: state.searchResults.totalFilteredResults,
    filters: state.filters,
    mapOpen: state.map.open,
});

const mapDispatchToProps = {
    setPage: filterActions.changePage,
    triggerFetch: fetchHotels,
};

export default connect(mapStateToProps, mapDispatchToProps)(Pagination);
